import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

import styles from './WeeklyCourseSchedule.module.scss';
import SelfStudyCard from './components/SelfStudyCard';
import LivePracticeCard from './components/LivePracticeCard';
import StudentProgressContainer from './components/StudentProgressContainer';
import SocialFeaturesContainer from './components/SocialFeaturesContainer';
import SocialFeaturesPopup from './components/SocialFeaturesPopup';
import IntroductoryLessonBookingModal from '../../new-booking/components/booking/IntroductoryLessonBookingModal';
import IntroductoryLessonCancelingModal from '../../new-booking/components/canceling/IntroductoryLessonCancelingModal';
import IntroductoryLessonReschedulingModal from '../../new-booking/components/rescheduling/IntroductoryLessonReschedulingModal';
import { getSocialFeatures } from '../../../actions/social-features';

/**
 * WeeklyCourseSchedule - Main container for the course schedule view
 * Includes course details, self-study, live practice, and social features
 */
const WeeklyCourseSchedule = ({
  courseDetailsRef,
  shouldOpenBookingIntroClassModal,
  shouldOpenCancelingModal,
  shouldOpenReschedulingModal,
  selectedCourse,
  sendToPlacementTest,
  sendToLessonsTab,
  isMobile,
  getSocialFeatures,
  socialFeaturesData
}) => {
  // Feature flags to determine which social feature to show
  const showPeerMilestones = useFeatureIsOn('peer-milestones');
  const showAchievementBadges = useFeatureIsOn('achievement-badges');
  const showStreakReminders = useFeatureIsOn('streak-reminders');

  // Only show a social feature if at least one feature flag is on
  const showSocialFeatures = showPeerMilestones || showAchievementBadges || showStreakReminders;

  // State for social features popup
  const [showSocialFeaturesPopup, setShowSocialFeaturesPopup] = useState(false);
  const [activeFeatureType, setActiveFeatureType] = useState(null);

  // Fetch social features data when the component mounts
  useEffect(() => {
    if (showSocialFeatures && selectedCourse?.id) {
      // Using mock data for now - when backend is ready, use getSocialFeatures instead
      getSocialFeatures(selectedCourse.id);
    }
  }, [showSocialFeatures, selectedCourse?.id, getSocialFeatures]);

  // Determine if popup should be shown based on feature flags and localStorage
  useEffect(() => {
    if (!showSocialFeatures || !socialFeaturesData?.data) return;

    const today = new Date().toLocaleDateString();

    // Logic for each feature type
    if (showPeerMilestones) {
      const lastShown = localStorage.getItem('peer-milestones-popup-shown');
      if (lastShown !== today) {
        setActiveFeatureType('peer-milestones');
        setShowSocialFeaturesPopup(true);
        return;
      }
    }

    // For achievement badges, in a real implementation this would be triggered by backend
    if (showAchievementBadges && socialFeaturesData.data.showBadgePopup) {
      const lastShown = localStorage.getItem('achievement-badges-popup-shown');
      if (lastShown !== today) {
        setActiveFeatureType('achievement-badges');
        setShowSocialFeaturesPopup(true);
        localStorage.setItem('achievement-badges-popup-shown', today);
        return;
      }
    }

    if (showStreakReminders && socialFeaturesData.data.streakDays > 0) {
      const lastShown = localStorage.getItem('streak-reminders-popup-shown');
      if (lastShown !== today) {
        setActiveFeatureType('streak-reminders');
        setShowSocialFeaturesPopup(true);
        return;
      }
    }
  }, [showSocialFeatures, socialFeaturesData, showPeerMilestones, showAchievementBadges, showStreakReminders]);

  const handleClosePopup = () => {
    setShowSocialFeaturesPopup(false);
  };

  return (
    <div className={styles['header-wrapper']} data-testid="weekly-course-schedule">
      <div className={styles['content-wrapper']}>
        <h1 className={styles['title']}>
          My <span id="dont-translate">{selectedCourse?.name}</span> schedule
        </h1>
        <div className={styles['content']}>
          <SelfStudyCard
            courseDetailsRef={courseDetailsRef}
            hasCompletedFinalExam={selectedCourse?.hasCompletedFinalExam}
            hasCompletedPracticeExam={selectedCourse?.hasCompletedPracticeExam}
            isTimeForExam={selectedCourse?.isTimeForExam}
            sendToPlacementTest={sendToPlacementTest}
            sendToLessonsTab={sendToLessonsTab}
            isMobile={isMobile}
          />
          <LivePracticeCard isTimeForExam={selectedCourse?.isTimeForExam} title="2. Live Practice" />
          <div className={styles['side-cards']}>
            <StudentProgressContainer />
            {showSocialFeatures && (
              <SocialFeaturesContainer
                showPeerMilestones={showPeerMilestones}
                showAchievementBadges={showAchievementBadges}
                showStreakReminders={showStreakReminders}
              />
            )}
          </div>
        </div>
      </div>

      {/* Social Features Popup */}
      <SocialFeaturesPopup isOpen={showSocialFeaturesPopup} onClose={handleClosePopup} featureType={activeFeatureType || ''} />

      {shouldOpenBookingIntroClassModal && <IntroductoryLessonBookingModal />}
      {shouldOpenCancelingModal && <IntroductoryLessonCancelingModal />}
      {shouldOpenReschedulingModal && <IntroductoryLessonReschedulingModal />}
    </div>
  );
};

const mapStateToProps = (state) => ({
  shouldOpenBookingIntroClassModal: state.newBooking.bookingLessons.shouldOpenBookingIntroClassModal,
  shouldOpenCancelingModal: state.newBooking.canceling.shouldOpenCancelingModal,
  shouldOpenReschedulingModal: state.newBooking.rescheduling.shouldOpenReschedulingModal,
  selectedCourse: state.courses?.selectedCourseDetails?.courseDetails,
  socialFeaturesData: state.socialFeaturesData
});

const mapDispatchToProps = {
  getSocialFeatures
};

WeeklyCourseSchedule.propTypes = {
  courseDetailsRef: PropTypes.object,
  isBlockedHowWasYourClass: PropTypes.bool,
  shouldOpenBookingIntroClassModal: PropTypes.bool,
  shouldOpenCancelingModal: PropTypes.bool,
  shouldOpenReschedulingModal: PropTypes.bool,
  isMobile: PropTypes.bool,
  selectedCourse: PropTypes.object,
  sendToPlacementTest: PropTypes.func,
  sendToLessonsTab: PropTypes.func,
  getSocialFeatures: PropTypes.func,
  socialFeaturesData: PropTypes.object
};

export default connect(mapStateToProps, mapDispatchToProps)(WeeklyCourseSchedule);
